import Typography from '@mui/material/Typography';
import { isBefore, startOfDay } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'shared/assets/icons';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { ResizableInput } from 'shared/components/ui/resizable-input';
import { useToday } from 'shared/contexts/today';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { useDelayedCallback } from 'shared/hooks/use-delayed-callback';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';

import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { MetaInteraction } from '../meta-interaction/meta-interaction';
import * as Styled from './meta-completion-per-day.style';

export type MetaCompletionPerDayProps = {
  schedules: HabitSchedule[];
  onChange: (schedules: HabitSchedule[]) => void;
};

export const MetaCompletionPerDay: React.FC<MetaCompletionPerDayProps> = ({
  schedules,
  onChange,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const focussedRef = useRef(false);

  const activeSchedule = useActiveSchedule(schedules);

  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();
  const [currentValue, setCurrentValue] = useState(
    activeSchedule?.completionTarget.count ?? 1,
  );
  const [delayedOnChange, killOnChangeDelay] = useDelayedCallback(
    onChange,
    500,
  );

  const changeSchedules = (val: number) => {
    if (!activeSchedule) {
      return null;
    }

    const endedSchedules = schedules?.filter(({ endDate }) => !!endDate) ?? [];
    const updateSchedules = [];
    const newSchedule: HabitSchedule = {
      ...activeSchedule,
      completionTarget: { ...activeSchedule.completionTarget, count: val },
    };

    const shouldReplaceSchedule =
      activeSchedule?.startDate &&
      !isBefore(startOfDay(activeSchedule.startDate), startOfDay(today));
    if (!shouldReplaceSchedule) {
      const endDate = new Date();
      !!activeSchedule &&
        updateSchedules.push({
          ...activeSchedule,
          endDate,
          plannedOccurrences: activeSchedule.plannedOccurrences?.filter(
            ({ date }) => isBefore(date, startOfDay(endDate)),
          ),
        });
      newSchedule.startDate = new Date();
    }

    updateSchedules.push(newSchedule);

    return [...endedSchedules, ...updateSchedules];
  };

  const onBlur = () => {
    focussedRef.current = false;
    if (
      !currentValue ||
      currentValue === activeSchedule?.completionTarget.count
    ) {
      return;
    }

    killOnChangeDelay();
    const updatedSchedules = changeSchedules(currentValue);
    updatedSchedules && onChange(updatedSchedules);
  };
  const onFocus = () => (focussedRef.current = true);

  const _onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Math.max(parseInt(e.target.value) || 1, 1);

    if (!activeSchedule) {
      return;
    }

    setCurrentValue(val);
    const updatedSchedules = changeSchedules(val);
    updatedSchedules && delayedOnChange(updatedSchedules);
  };

  useEffect(() => {
    if (!focussedRef.current) {
      const activeSchedule = getActiveSchedule(schedules);
      setCurrentValue(activeSchedule?.completionTarget?.count ?? 1);
    }
  }, [schedules]);

  useKeysDown(
    ['Enter'],
    (e) => {
      if (document.activeElement === inputRef.current) {
        e.preventDefault();
        killOnChangeDelay();
        closeMenu();
      }
    },
    { otherKeysInCombinationAllowed: true },
  );

  return (
    <>
      <MetaInteraction
        ref={buttonRef}
        icon={CheckCircle}
        placeholder=""
        label={t('forms.habit.fields.count-completions-per-day.description', {
          count: currentValue,
        })}
        onClick={openMenu}
        selected={menuOpen}
      />

      {menuOpen && (
        <PopupMenu referenceElement={buttonRef} onOutside={closeMenu}>
          <PopupMenuList>
            <PopupMenuListItem>
              <Styled.ListItem>
                <ResizableInput
                  ref={inputRef}
                  onChange={_onChange}
                  value={currentValue}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  showAsSmallFormInput
                  type="number"
                />
                <Typography variant="body2">
                  {t('forms.habit.fields.completions-per-day.description', {
                    count: currentValue,
                  })}
                </Typography>
              </Styled.ListItem>
            </PopupMenuListItem>
          </PopupMenuList>
        </PopupMenu>
      )}
    </>
  );
};
